import { useState, useEffect, useRef } from 'react';
import { usePortfolio } from '../../context/PortfolioContext';
import { tickersApi } from '../../api/client';
import TickerSearchInput from '../TickerSearchInput';

function TradeModal({ isOpen, onClose, trade = null }) {
  const { createTrade, updateTrade } = usePortfolio();
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    ticker: '',
    display_name: '',
    side: 'BUY',
    quantity: '',
    price: '',
    currency: 'USD',
    broker: '',
    commission_gbp: 0,
    fx_to_gbp: '',
  });
  
  // Track if form has been initialized to prevent resets on global refresh
  const isInitializedRef = useRef(false);
  const prevIsOpenRef = useRef(false);
  const [, setSelectedMapping] = useState(null);

  useEffect(() => {
    // Only initialize form when modal opens (isOpen transitions from false to true)
    // This prevents form reset when global state refreshes or trade reference changes
    const isOpening = isOpen && !prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;
    
    if (!isOpen) {
      // Modal closed - reset initialization flag for next time
      isInitializedRef.current = false;
      hasInteractedRef.current = false;
      return;
    }
    
    // CRITICAL: Never reset form if user has started typing
    if (hasInteractedRef.current) {
      return;
    }
    
    if (!isOpening || isInitializedRef.current) {
      // Skip if not opening or already initialized
      return;
    }
    
    // Mark as initialized
    isInitializedRef.current = true;
    
    if (trade) {
      setFormData({
        date: trade.date || new Date().toISOString().split('T')[0],
        ticker: trade.ticker || '',
        display_name: trade.display_name || '',
        side: trade.side || 'BUY',
        quantity: trade.quantity || '',
        price: trade.price || '',
        currency: trade.currency || 'USD',
        broker: trade.broker || '',
        commission_gbp: trade.commission_gbp || 0,
        fx_to_gbp: trade.fx_to_gbp || '',
      });
    } else {
      // Reset form for new trade
      setFormData({
        date: new Date().toISOString().split('T')[0],
        ticker: '',
        display_name: '',
        side: 'BUY',
        quantity: '',
        price: '',
        currency: 'USD',
        broker: '',
        commission_gbp: 0,
        fx_to_gbp: '',
      });
    }
  }, [trade, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      quantity: Number(formData.quantity),
      price: Number(formData.price),
      commission_gbp: Number(formData.commission_gbp),
      fx_to_gbp: formData.fx_to_gbp ? Number(formData.fx_to_gbp) : undefined,
    };

    // Close modal immediately on success, don't wait for full refresh
    const success = trade 
      ? await updateTrade(trade.id, data)
      : await createTrade(data);

    if (success) {
      // Close immediately - data refresh happens in background
      onClose();
    }
  };

  // Track if user has interacted with form to prevent any resets
  const hasInteractedRef = useRef(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (!hasInteractedRef.current && value) {
      hasInteractedRef.current = true;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Ticker-specific change handler used by TickerSearchInput
  const handleTickerChange = (upperValue) => {
    if (!hasInteractedRef.current && upperValue) {
      hasInteractedRef.current = true;
    }
    setFormData(prev => ({ ...prev, ticker: upperValue }));
    setSelectedMapping(null);
  };

  // Select rich result and persist mapping
  const selectSuggestion = async (suggestion) => {
    setSelectedMapping(suggestion);

    setFormData(prev => ({
      ...prev,
      ticker: suggestion.symbol || suggestion.internal_ticker,
      currency: suggestion.currency || 'USD',
      display_name: suggestion.long_name || suggestion.short_name || suggestion.name || '',
    }));

    if (suggestion.internal_ticker || suggestion.symbol) {
      const mappingData = {
        internal_ticker: suggestion.internal_ticker || formData.ticker,
        source_symbol: suggestion.symbol,
        yahoo_ticker: suggestion.symbol,
        display_name: suggestion.long_name || suggestion.short_name || suggestion.symbol,
        price_currency: suggestion.currency || 'USD',
        exchange: suggestion.exchange,
        instrument_type: suggestion.type || 'EQUITY',
        region: suggestion.country,
      };
      try {
        await tickersApi.saveMapping(mappingData);
      } catch (e) {
        console.error('Failed to save ticker mapping:', e);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{trade ? 'Edit Trade' : 'New Trade'}</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-group">
                <label>Date</label>
                <input 
                  type="date" 
                  name="date" 
                  value={formData.date} 
                  onChange={handleChange}
                  required 
                />
              </div>
              <div className="form-group">
                <label>Ticker</label>
                <TickerSearchInput
                  value={formData.ticker}
                  onChange={handleTickerChange}
                  onSelectSuggestion={selectSuggestion}
                  required
                  rich
                />
              </div>
            </div>
            
            <div className="form-grid">
              <div className="form-group">
                <label>Side</label>
                <select name="side" value={formData.side} onChange={handleChange}>
                  <option value="BUY">BUY</option>
                  <option value="SELL">SELL</option>
                </select>
              </div>
              <div className="form-group">
                <label>Currency</label>
                <select name="currency" value={formData.currency} onChange={handleChange}>
                  <option value="USD">USD</option>
                  <option value="GBP">GBP</option>
                  <option value="EUR">EUR</option>
                  <option value="CAD">CAD</option>
                </select>
              </div>
            </div>
            
            <div className="form-grid">
              <div className="form-group">
                <label>Quantity</label>
                <input 
                  type="number" 
                  name="quantity" 
                  value={formData.quantity} 
                  onChange={handleChange}
                  step="0.01"
                  required 
                />
              </div>
              <div className="form-group">
                <label>Price</label>
                <input 
                  type="number" 
                  name="price" 
                  value={formData.price} 
                  onChange={handleChange}
                  step="0.0001"
                  required 
                />
              </div>
            </div>
            
            <div className="form-group">
              <label>FX to GBP (optional - will fetch if empty)</label>
              <input 
                type="number" 
                name="fx_to_gbp" 
                value={formData.fx_to_gbp} 
                onChange={handleChange}
                step="0.000001"
                placeholder="e.g. 0.79 for USD"
              />
            </div>
            
            <div className="form-grid">
              <div className="form-group">
                <label>Broker</label>
                <input 
                  type="text" 
                  name="broker" 
                  value={formData.broker} 
                  onChange={handleChange}
                  placeholder="e.g. IBKR"
                />
              </div>
              <div className="form-group">
                <label>Commission (GBP)</label>
                <input 
                  type="number" 
                  name="commission_gbp" 
                  value={formData.commission_gbp} 
                  onChange={handleChange}
                  step="0.01"
                />
              </div>
            </div>
          </div>
          
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {trade ? 'Update' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default TradeModal;
